import { isEqual, omit } from 'lodash-es';
import { parseBoolean } from '@gitlab/frontend-utils';
import AccessorUtilities from '~/lib/utils/accessor';
import { historyPushState } from '~/lib/utils/common_utils';
import { queryToObject, mergeUrlParams, removeParams } from '~/lib/utils/url_utility';
import { s__ } from '~/locale';
import BoardContent from './board_content.vue?vue3';
import BoardSettingsSidebar from './board_settings_sidebar.vue?vue3';
import BoardTopBar from './board_top_bar.vue?vue3';
import { listsQuery, FilterFields, GroupByParamType } from 'ee_else_ce/boards/constants';
import { formatBoardLists, filterVariables, FiltersInfo } from 'ee_else_ce/boards/boards_util';
import activeBoardItemQuery from 'ee_else_ce/boards/graphql/client/active_board_item.query.graphql';
import errorQuery from '../graphql/client/error.query.graphql';
import { setError } from '../graphql/cache_updates.js?vue3';
export default {
    name: 'BoardApp',
    i18n: {
        fetchError: s__('Boards|An error occurred while fetching the board lists. Please reload the page.')
    },
    components: {
        BoardContent,
        BoardSettingsSidebar,
        BoardTopBar
    },
    inject: [
        'fullPath',
        'initialBoardId',
        'initialFilterParams',
        'isIssueBoard',
        'isGroupBoard',
        'issuableType',
        'boardType',
        'hasCustomFieldsFeature'
    ],
    data () {
        return {
            activeBoardItem: undefined,
            boardLists: {},
            activeListId: '',
            boardId: this.initialBoardId,
            filterParams: {
                ...this.initialFilterParams
            },
            addColumnFormVisible: false,
            isShowingEpicsSwimlanes: false,
            error: null,
            isWorkItemDrawerOpened: false
        };
    },
    apollo: {
        activeBoardItem: {
            query: activeBoardItemQuery,
            variables () {
                return {
                    isIssue: this.isIssueBoard
                };
            },
            result ({ data: { activeBoardItem } }) {
                if (activeBoardItem && activeBoardItem.listId !== null) {
                    this.setActiveId('');
                }
            }
        },
        boardLists: {
            query () {
                return listsQuery[this.issuableType].query;
            },
            variables () {
                return this.listQueryVariables;
            },
            update (data) {
                const { lists } = data[this.boardType].board;
                return formatBoardLists(lists);
            },
            error (error) {
                setError({
                    error,
                    message: this.$options.i18n.fetchError
                });
            }
        },
        error: {
            query: errorQuery,
            update: (data)=>data.boardsAppError
        }
    },
    computed: {
        listQueryVariables () {
            return {
                ...this.isIssueBoard && {
                    isGroup: this.isGroupBoard,
                    isProject: !this.isGroupBoard
                },
                fullPath: this.fullPath,
                boardId: this.boardId,
                filters: this.formattedFilterParams
            };
        },
        isSwimlanesOn () {
            return (gon?.licensed_features?.swimlanes && this.isShowingEpicsSwimlanes) ?? false;
        },
        isAnySidebarOpen () {
            return this.activeBoardItem?.id || this.activeListId;
        },
        activeList () {
            return this.activeListId ? this.boardLists[this.activeListId] : undefined;
        },
        formattedFilterParams () {
            return filterVariables({
                filters: omit(this.filterParams, 'groupBy'),
                issuableType: this.issuableType,
                filterInfo: FiltersInfo,
                filterFields: FilterFields,
                options: {
                    hasCustomFieldsFeature: this.hasCustomFieldsFeature
                }
            });
        },
        isShowingEpicSwimlanesLocalStorageKey () {
            return `board.${this.fullPath}.${this.boardId}.isShowingEpicSwimlanes`;
        },
        isBoardWidthDynamic () {
            return this.isAnySidebarOpen && this.isWorkItemDrawerOpened;
        }
    },
    created () {
        this.initIsShowingEpicSwimlanes();
    },
    methods: {
        refetchLists () {
            this.$apollo.queries.boardLists.refetch();
        },
        setActiveId (id) {
            this.activeListId = id;
            if (!id && !this.isAnySidebarOpen) this.isWorkItemDrawerOpened = false;
        },
        switchBoard (id) {
            this.boardId = id;
            this.setActiveId('');
            this.setIsShowingEpicSwimlanesFromLocalStorage();
        },
        setFilters (filters) {
            const filterParams = {
                ...filters
            };
            this.filterParams = filterParams;
        },
        submitFilters (filters) {
            const previousVariables = this.listQueryVariables;
            this.setFilters(filters);
            if (isEqual(previousVariables, this.listQueryVariables)) {
                this.refetchBoard();
            }
        },
        refetchBoard () {
            const typename = this.isIssueBoard ? 'BoardList' : 'EpicList';
            const itemsFieldName = `${this.issuableType}s`;
            const countFieldName = this.isIssueBoard ? 'issuesCount' : 'metadata';
            this.$apollo.getClient().refetchQueries({
                include: [
                    listsQuery[this.issuableType].query
                ],
                updateCache: (cache)=>{
                    Object.keys(this.boardLists).forEach((listId)=>{
                        const id = cache.identify({
                            __typename: typename,
                            id: listId
                        });
                        cache.evict({
                            id,
                            fieldName: itemsFieldName
                        });
                        cache.evict({
                            id,
                            fieldName: countFieldName
                        });
                    });
                    if (this.isSwimlanesOn) {
                        cache.evict({
                            id: cache.identify({
                                __typename: 'Board',
                                id: this.boardId
                            }),
                            fieldName: 'epics'
                        });
                    }
                }
            });
        },
        setIsShowingEpicSwimlanes (value) {
            this.isShowingEpicsSwimlanes = value;
            this.saveIsShowingEpicSwimlanes();
        },
        getIsShowingEpicSwimlanesFromUrl () {
            return queryToObject(window.location.search).group_by === GroupByParamType.epic;
        },
        getIsShowingEpicSwimlanesFromLocalStorage () {
            return parseBoolean(localStorage.getItem(this.isShowingEpicSwimlanesLocalStorageKey));
        },
        setIsShowingEpicSwimlanesFromLocalStorage () {
            if (AccessorUtilities.canUseLocalStorage()) {
                this.isShowingEpicsSwimlanes = this.getIsShowingEpicSwimlanesFromLocalStorage();
                if (this.isShowingEpicsSwimlanes) {
                    historyPushState(mergeUrlParams({
                        group_by: GroupByParamType.epic
                    }, window.location.href, {
                        spreadArrays: true
                    }));
                } else {
                    this.removeGroupByParam();
                }
            }
        },
        initIsShowingEpicSwimlanes () {
            if (this.isIssueBoard) {
                const urlHasEpicSwimlanes = this.getIsShowingEpicSwimlanesFromUrl();
                this.setIsShowingEpicSwimlanes(urlHasEpicSwimlanes);
                if (urlHasEpicSwimlanes) {
                    return;
                }
                if (this.getIsShowingEpicSwimlanesFromLocalStorage()) {
                    this.setIsShowingEpicSwimlanes(true);
                }
            } else {
                this.removeGroupByParam();
            }
        },
        saveIsShowingEpicSwimlanes () {
            if (AccessorUtilities.canUseLocalStorage()) {
                const currentLocalStorageValue = this.getIsShowingEpicSwimlanesFromLocalStorage();
                if (currentLocalStorageValue !== this.isShowingEpicsSwimlanes) {
                    localStorage.setItem(this.isShowingEpicSwimlanesLocalStorageKey, this.isShowingEpicsSwimlanes);
                }
            }
        },
        removeGroupByParam () {
            historyPushState(removeParams([
                'group_by'
            ]), window.location.href, true);
        },
        handleWorkItemDrawerClose () {
            if (!this.isAnySidebarOpen) {
                this.isWorkItemDrawerOpened = false;
            }
        }
    }
};
